import React from 'react';
import { find, isNil, noop, size, defaultTo, map, get, every, isEmpty, isEqual } from 'lodash';

// local dependencies
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION, CALL_TAGGING_MODES, CALL_RESOLUTION_STATUS, SIZES } from 'constants/common';
import { CallDispositionReasonsService } from 'services/callDispositionReasons';
import { callDispositionReasons as CallDispositionReasonsView } from 'ui/views/widgets/callDispositionReasons';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as confirmOverlay from 'utils/confirmOverlay';
import { getLabel } from 'utils/getLabel';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CallDispositionReasonsWidget } from 'ui/containers/CallDispositionReasonsWidget';
 *
 * // render
 * <CallDispositionReasonsWidget propName='propValue'/>
 */
export class CallDispositionReasonsWidget extends React.Component {

	/**
	 * @param {object} props - props
	 */
	constructor( props ) {
		super( props );

		// bind methods to instance
		this.__bind();

		// component state
		this.state = {
			isFetching: true,
			networkStatus: undefined,
			componentData: undefined,
			mode: 'readonly', // could be `readonly` or `editable`
			editItemId: null, // when an item is in edit mode
		};

		// create service instance
		this.callDispositionReasonsService = new CallDispositionReasonsService();

		// start component data fetch
		this.fetchComponentData( this.props.memberId );
	}

	/*********************************************/
	
	UNSAFE_componentWillReceiveProps ( newProps ) {
		log.debug( 'CallDispositionReasonsWidget.componentWillReceiveProps()' );

		// if `locations` changes, re-render map
		if( ! isEqual( newProps.memberId, this.props.memberId ) ) {

			this.updateWidgetData( newProps );

		}
	}

	// bind methods and events to instance
	__bind() {
		this.handleToggleEditMode = this.handleToggleEditMode.bind( this );
		this.handleFormSubmit = this.handleFormSubmit.bind( this );
		this.handleDeleteClick = this.handleDeleteClick.bind( this );
		this.updateWidgetData = this.updateWidgetData.bind( this );
	}

	/*********************************************/

	updateWidgetData( newProps ) {
		log.debug( 'CallDispositionReasonsWidget.updateWidgetData()' );

		this.setState( {
			isFetching: true
		}, () => {

			// start component data fetch
			this.fetchComponentData( newProps.memberId ); // re-render map if map dependencies are loaded
		} );

	}

	/**
	 * @desc Fetch component response.
	 */
	async fetchComponentData( memberId ) {
		log.debug( 'CallDispositionReasonsWidget.fetchComponentData()' );

		// cancel ongoing request
		this.callDispositionReasonsService.cancel();

		// get status and service data
		const {
			status,
			data,
		} = await this.callDispositionReasonsService.fetchComponentData( {
			url: this.props.widget.url,
			data: {
				memberId: memberId,
				patientId: this.props.patientId
			}
		} );

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState(
				{
					isFetching: false,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
					componentData: data,
				},
				() => {

					// info validation status
					this.props.onCallCategoryAndReasonsChange( {
						isValid: 0 < size( this.state.componentData.items ),
						callResolution: this.getCallResolution( this.state.componentData.items, this.state.componentData.options ),
					} );
				}
			);
		}
	}

	/**
	 * @desc Toggle between read and edit mode.
	 */
	handleToggleEditMode( editable = true, editItemId = null ) {
		log.debug( 'CallDispositionReasons.handleToggleEditMode()' );

		this.setState( {
			mode: editable ? 'editable' : 'readonly',
			editItemId, // when `edit` action is clicked
		} );
	}

	/**
	 * @desc Handle form submit.
	 */
	async handleFormSubmit( { id = null, category, reason } = {} ) {
		log.debug( 'CallDispositionReasons.handleFormSubmit()' );

		// get save item action
		const saveAction = find( this.state.componentData.actions, { name: 'save' } );

		// cancel ongoing request
		this.callDispositionReasonsService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const { status, data } = await this.callDispositionReasonsService.addUpdateItem( {
			url: saveAction.url,
			data: {
				operation: isNil( id ) ? CRUD_OPERATION.CREATE : CRUD_OPERATION.UPDATE,
				id,
				category: category.value,
				reason: reason.value,
				memberId: this.props.memberId
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

			this.setState(
				{
					isFetching: true,
					mode: 'readonly',
					editItemId: null,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
				},
				() => {

					// reload the widget
					this.fetchComponentData( this.props.memberId );

					// info validation status
					this.props.onCallCategoryAndReasonsChange( {
						isValid: 0 < size( this.state.componentData.items ),
						callResolution: this.getCallResolution( this.state.componentData.items, this.state.componentData.options ),
					} );

				}
			);
		} else {

			if( !isEmpty( data.description ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL } ); // show error message
			} else {
				errorOverlay.open(); // show error
			}
		}
	}

	/**
	 * @desc Handle item `delete` action.
	 */
	async handleDeleteClick( { id } = {} ) {
		log.debug( 'CallDispositionReasons.handleDeleteClick()', id );

		// get delete item action
		const deleteAction = find( this.state.componentData.actions, {
			name: 'delete',
		} );

		const shouldProceed = await confirmOverlay.open( {
			message: getLabel( deleteAction.confirmation ),
			iconName: 'close',
		} );

		if ( shouldProceed ) {

			// get delete item action
			//  const deleteAction = find(  this.state.componentData.actions, { name: 'delete' } );

			// cancel ongoing request
			this.callDispositionReasonsService.cancel();

			// show page level preloader
			pageLevelPreloader.show();

			// get status and service data
			const { status } = await this.callDispositionReasonsService.deleteItem( {
				url: deleteAction.url,
				data: {
					id: id,
					memberId: this.props.memberId
				},
			} );

			// hide page level preloader
			pageLevelPreloader.hide();

			// check if response status was success
			if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

				this.setState(
					{
						isFetching: true,
						networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
					},
					() => {
						this.fetchComponentData( this.props.memberId );
						this.props.onCallCategoryAndReasonsChange( {
							isValid: 0 < size( this.state.componentData.items ),
							callResolution: this.getCallResolution( this.state.componentData.items, this.state.componentData.options ),
						} );
						
					}
				);
			} else {
				errorOverlay.open(); // show error
			}
		}
	}

	/**
	 * @desc Find call automatic (preferred) resolution from the selected category and reasons.
	 */
	getCallResolution( items = [], options = [] ) {
		log.debug( 'CallDispositionReasons.getCallResolution()' );

		// if `items` are empty, return `null`
		if( isEmpty( items ) ) {
			return null; // no auto call resolution
		}

		// extract call tagging and resolution
		const data = map( items, item => {

			// get options
			const categoryOption = defaultTo( find( options, { value: item.category } ), {} );
			const reasonOption = defaultTo( find( categoryOption.options, { value: item.reason } ), {} );

			return {
				tagging: get( reasonOption, 'config.tagging' ),
				resolution: get( reasonOption, 'config.resolution' ),
			};
		} );

		// check if `data` contains any entry for manual tagging
		if( find( data, { tagging: CALL_TAGGING_MODES.MANUAL } ) ) {
			return null; // no auto call resolution
		}

		// if every resolution is `resolved`, then status is `resolved`, else `unresolved`
		if( every( data, ( item ) => CALL_RESOLUTION_STATUS.RESOLVED === item.resolution ) ) {
			return CALL_RESOLUTION_STATUS.RESOLVED;
		} else {
			return CALL_RESOLUTION_STATUS.UNRESOLVED;
		}

	}

	/*********************************************/

	/**
	 * @desc When component unmounts, cleanup resouces.
	 */
	componentWillUnmount() {
		this.callDispositionReasonsService.cancel(); // cancel ongoing requests
	}

	/**
	 * @desc Render a React view component
	 */
	render() {
		return (
			<CallDispositionReasonsView
				isFetching={this.state.isFetching}
				networkStatus={this.state.networkStatus}
				mode={this.state.mode}
				editItemId={this.state.editItemId}
				{...this.state.componentData}
				onToggleEditMode={this.handleToggleEditMode}
				onFormSubmit={this.handleFormSubmit}
				onDeleteClick={this.handleDeleteClick}
			/>
		);
	}
}

// set display name
CallDispositionReasonsWidget.displayName = 'CallDispositionReasonsWidget';

// set default props
CallDispositionReasonsWidget.defaultProps = {
	onCallCategoryAndReasonsChange: noop,
};

// prop types
CallDispositionReasonsWidget.propTypes = {};
